import React, { useCallback, useEffect, useMemo, useState } from "react";

import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { AccordionDetails, Box, Popper, Typography } from "@mui/material";
import { IQuickViewMeasures } from "store/types";

interface Props {
  item: IQuickViewMeasures;
  scroll: boolean;
  selectedItems: string[];
  setMeasure: (cb: (prevState: string[]) => string[]) => void;
  container?: Element | null;
}

export const AccordionMeasureItem: React.FC<Props> = ({ item, scroll, selectedItems, setMeasure, container }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [itemRef, setItemRef] = useState(null);
  const handleHover = (event: any) => {
    setIsOpen(!isOpen);
    setItemRef(event.currentTarget);
  };

  const isLastActive = useMemo<boolean>(() => {
    let result = false;

    if (item.types.includes("last")) {
      result = selectedItems.includes(`last${item.id}`);
    }

    return result;
  }, [selectedItems]);

  const isSumActive = useMemo<boolean>(() => {
    return selectedItems.includes(item.id);
  }, [selectedItems]);

  const isActiveMeasure = isLastActive || isSumActive;

  const handleMeasureClick = useCallback(
    ({ id, isLast }: { id: string; isLast: boolean }) => {
      setMeasure((prevState) => {
        if (isLast) {
          id = `last${id}`;
        }
        return prevState.includes(id) ? prevState.filter((item) => item !== id) : [...prevState, id];
      });
    },
    [setMeasure]
  );

  useEffect(() => {
    setIsOpen(false);
  }, [scroll]);

  return (
    <AccordionDetails
      ref={itemRef}
      onMouseEnter={(e) => handleHover(e)}
      onMouseLeave={() => setIsOpen(false)}
      sx={{
        cursor: "pointer",
        "&:hover": { background: "#DBE8F5" },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        ...(isOpen && {
          background: "#DBE8F5"
        }),
        ...(isActiveMeasure && {
          background: "#DBE8F5"
        })
      }}
    >
      {item.name}
      <KeyboardArrowRightIcon />
      {item?.types?.length && (
        <Popper placement="left" open={isOpen} anchorEl={itemRef} sx={{ zIndex: 1300 }} container={container ?? document.body}>
          <Box
            sx={{
              overflow: "hidden",
              background: "#fff",
              borderRadius: "8px 0px 0px 8px",
              boxShadow:
                "0px 3px 3px -2px rgba(38, 54, 70, 0.16), 0px 3px 4px 0px rgba(38, 54, 70, 0.10), 0px 1px 8px 0px rgba(38, 54, 70, 0.08)"
            }}
          >
            {item?.types?.includes("last") && (
              <Typography
                sx={{
                  width: "100px",
                  "&:hover": { background: "#DBE8F5", cursor: "pointer" },
                  padding: "5px 10px",
                  ...(isLastActive && {
                    background: "#DBE8F5"
                  })
                }}
                onClick={() => handleMeasureClick({ id: item.id, isLast: true })}
              >
                Latest
              </Typography>
            )}
            {item?.types?.includes("sum") && (
              <Typography
                sx={{
                  width: "100px",
                  "&:hover": { background: "#DBE8F5", cursor: "pointer" },
                  padding: "5px 10px",
                  ...(isSumActive && {
                    background: "#DBE8F5"
                  })
                }}
                onClick={() => handleMeasureClick({ id: item.id, isLast: false })}
              >
                Sum
              </Typography>
            )}
          </Box>
        </Popper>
      )}
    </AccordionDetails>
  );
};
